import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

// Create the Toast Context
const ToastContext = createContext(null);

/**
 * Toast Provider Component
 * Manages toast notifications globally across the app
 */
export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);

    // Use ref to avoid dependency issues
    const removeToastRef = useRef(null);

    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);

    removeToastRef.current = removeToast;

    const addToast = useCallback((message, type = 'info', duration = 5000, options = {}) => {
        const id = Date.now() + Math.random();

        const newToast = {
            id,
            message,
            type, // 'success', 'error', 'warning', 'info'
            duration,
            showUndo: options.showUndo || false,
            onUndo: options.onUndo || null,
            ...options
        };

        setToasts(prev => [...prev, newToast]);

        if (duration > 0) {
            setTimeout(() => {
                removeToastRef.current?.(id);
            }, duration);
        }

        return id;
    }, []);

    const success = useCallback((message, options = {}) => {
        return addToast(message, 'success', options.duration || 5000, options);
    }, [addToast]);

    const error = useCallback((message, options = {}) => {
        return addToast(message, 'error', options.duration || 7000, options);
    }, [addToast]);

    const warning = useCallback((message, options = {}) => {
        return addToast(message, 'warning', options.duration || 6000, options);
    }, [addToast]);

    const info = useCallback((message, options = {}) => {
        return addToast(message, 'info', options.duration || 5000, options);
    }, [addToast]);

    const clearAll = useCallback(() => {
        setToasts([]);
    }, []);

    const value = {
        toasts,
        addToast,
        removeToast,
        success,
        error,
        warning,
        info,
        clearAll
    };

    return (
        <ToastContext.Provider value={value}>
            {children}
        </ToastContext.Provider>
    );
}

/**
 * Custom hook to use toast context
 * @returns {Object} Toast context value
 */
export function useToast() {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
}

export default ToastContext;
